'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import CountUp from 'react-countup';
import useGeneralSounds from '@/hooks/useGeneralSounds';
import { summarizeSCBalances } from '@/libs/summarizeSCBalances';
import { useWalletMeQuery } from '@/queries/wallet/wallet.queries';
import { formatCurrencyWithStableDigitWidth } from '@/utils/currency.utils';

type BalanceType = 'GC' | 'SC';

interface Props {
  type: BalanceType;
}

function AnimatedBalance({ type = 'SC' }: Props) {
  const { data: coins, isLoading } = useWalletMeQuery();
  const [soundsEnabled] = useGeneralSounds();
  const [end, setEnd] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!soundsEnabled) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      return;
    }

    if (!audioRef.current) {
      const audio = new Audio('/sounds/coin-drop.mp3');
      audio.preload = 'auto';
      audioRef.current = audio;
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [soundsEnabled]);

  const chooseCoins = useMemo(() => {
    if (coins) {
      return type === 'GC'
        ? Number(coins.goldCoin)
        : summarizeSCBalances(Number(coins.sweepCoin), Number(coins.bonusSweepCoin));
    }

    return 0;
  }, [coins, type]);
  const isInitializedPerTypeRef = useRef<Record<BalanceType, boolean>>({ SC: false, GC: false });

  useEffect(() => {
    if (isLoading || !coins) {
      return;
    }

    if (isInitializedPerTypeRef.current[type]) {
      return;
    }

    const currentValue = chooseCoins;

    // `start` is pinned at 0, so moving `end` here counts up from zero to the real
    // balance on every mount — page load, and each GC/SC toggle via `key={currency}`.
    setEnd(currentValue / 100);
    localStorage.setItem(type, String(currentValue));

    isInitializedPerTypeRef.current[type] = true;
  }, [coins, isLoading, chooseCoins, type]);

  useEffect(() => {
    if (isLoading || !isInitializedPerTypeRef.current[type]) {
      return;
    }

    const currentValue = chooseCoins;
    const savedValue = localStorage.getItem(type);
    const previousValue = savedValue ? Number(savedValue) : currentValue;

    if (currentValue === previousValue) {
      return;
    }

    if (currentValue > previousValue) {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        void audio.play();
      }
    }

    // Only `end` is driven after mount. Changing `start` would make react-countup
    // recreate its instance from that value, which ignores `preserveValue` and
    // snaps the display instead of animating from wherever it currently is.
    setEnd(currentValue / 100);
    localStorage.setItem(type, String(currentValue));
  }, [chooseCoins, isLoading, type, coins]);

  return (
    <CountUp
      className="tracking-[-0.0022rem]!"
      start={0}
      end={end}
      duration={1.5}
      decimals={2}
      formattingFn={formatCurrencyWithStableDigitWidth}
      preserveValue
    />
  );
}

export default AnimatedBalance;
